import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Plot from 'react-plotly.js';
import Plotly from 'plotly.js/dist/plotly';
import { Box, Button } from '@material-ui/core';
import { GetApp } from '@material-ui/icons';

const RMS_COLOR = '#1976d2';
const BIAS_COLOR = '#d32f2f';

const IMAGE_EXPORT_OPTIONS = { format: 'png', width: 1200, height: 500, scale: 2 };

/**
 * @function ShoreSErrorPlots
 * @description Renders the two Shore-S validation plots from
 * `state.shoreSAnalysis.aggregatedMetrics`: RMS/bias of the model error over
 * time (per modeled date) and per transect. Each plot carries labeled axes
 * with units, a horizontal legend and an "export as image" button (plus
 * Plotly's built-in toolbar camera).
 * **Called by:
 * @see {ShoreSAnalysisSteps}
 * @memberof module:ShoreS
 * @returns {JSX.Element|null} The two plots, or null before a validation run completes.
 */
const ShoreSErrorPlots = () => {
  const { t } = useTranslation();
  const aggregatedMetrics = useSelector((s) => s.shoreSAnalysis.aggregatedMetrics);

  // Rendered graph divs, captured so the export buttons can call
  // `Plotly.downloadImage` on the exact on-screen figures.
  const byDatePlotRef = useRef(null);
  const byTransectPlotRef = useRef(null);

  if (!aggregatedMetrics) return null;

  const { byDate, byTransect } = aggregatedMetrics;

  const exportImage = (plotRef, filename) => {
    if (!plotRef.current) return;
    Plotly.downloadImage(plotRef.current, { ...IMAGE_EXPORT_OPTIONS, filename });
  };

  const baseLayout = {
    autosize: true,
    legend: { orientation: 'h', x: 0, y: 1.14, yanchor: 'bottom' },
    margin: { t: 70, r: 30 },
    yaxis: {
      title: { text: t('forms.shoreS.analysis.plotErrorAxis') },
      zeroline: true,
      zerolinecolor: '#9e9e9e',
      zerolinewidth: 1,
    },
  };

  const baseConfig = (filename) => ({
    responsive: true,
    toImageButtonOptions: { ...IMAGE_EXPORT_OPTIONS, filename },
  });

  const exportButton = (plotRef, filename) => (
    <Box display="flex" justifyContent="flex-end">
      <Button
        size="small"
        color="secondary"
        startIcon={<GetApp />}
        onClick={() => exportImage(plotRef, filename)}
      >
        {t('forms.shoreS.analysis.plotExportImage')}
      </Button>
    </Box>
  );

  return (
    <Box mt={2}>
      <Plot
        data={[
          {
            x: byDate.map((d) => d.time),
            y: byDate.map((d) => d.rms),
            type: 'scatter',
            mode: 'lines+markers',
            name: t('forms.shoreS.analysis.plotRmsSeries'),
            marker: { color: RMS_COLOR },
            line: { color: RMS_COLOR },
            hovertemplate: '%{x|%d/%m/%Y}: %{y:.2f} m<extra>RMS</extra>',
          },
          {
            x: byDate.map((d) => d.time),
            y: byDate.map((d) => d.bias),
            type: 'scatter',
            mode: 'lines+markers',
            name: t('forms.shoreS.analysis.plotBiasSeries'),
            marker: { color: BIAS_COLOR },
            line: { color: BIAS_COLOR, dash: 'dot' },
            hovertemplate: '%{x|%d/%m/%Y}: %{y:.2f} m<extra>Bias</extra>',
          },
        ]}
        layout={{
          ...baseLayout,
          title: { text: t('forms.shoreS.analysis.plotByDateTitle') },
          xaxis: { title: { text: t('forms.shoreS.analysis.plotDateAxis') }, type: 'date' },
        }}
        style={{ width: '100%', height: '360px' }}
        config={baseConfig('shore-s-erro-por-data')}
        onInitialized={(figure, graphDiv) => {
          byDatePlotRef.current = graphDiv;
        }}
        onUpdate={(figure, graphDiv) => {
          byDatePlotRef.current = graphDiv;
        }}
      />
      {exportButton(byDatePlotRef, 'shore-s-erro-por-data')}

      <Plot
        data={[
          {
            x: byTransect.map((tr) => tr.transectId),
            y: byTransect.map((tr) => tr.rms),
            type: 'bar',
            name: t('forms.shoreS.analysis.plotRmsSeries'),
            marker: { color: RMS_COLOR },
            hovertemplate: `${t('forms.shoreS.analysis.plotTransectShort')} %{x}: %{y:.2f} m<extra>RMS</extra>`,
          },
          {
            x: byTransect.map((tr) => tr.transectId),
            y: byTransect.map((tr) => tr.bias),
            type: 'bar',
            name: t('forms.shoreS.analysis.plotBiasSeries'),
            marker: { color: BIAS_COLOR },
            hovertemplate: `${t('forms.shoreS.analysis.plotTransectShort')} %{x}: %{y:.2f} m<extra>Bias</extra>`,
          },
        ]}
        layout={{
          ...baseLayout,
          title: { text: t('forms.shoreS.analysis.plotByTransectTitle') },
          xaxis: { title: { text: t('forms.shoreS.analysis.plotTransectAxis') } },
        }}
        style={{ width: '100%', height: '360px' }}
        config={baseConfig('shore-s-erro-por-transecto')}
        onInitialized={(figure, graphDiv) => {
          byTransectPlotRef.current = graphDiv;
        }}
        onUpdate={(figure, graphDiv) => {
          byTransectPlotRef.current = graphDiv;
        }}
      />
      {exportButton(byTransectPlotRef, 'shore-s-erro-por-transecto')}
    </Box>
  );
};

export default ShoreSErrorPlots;
